import React, { useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDateDE } from "@/lib/playerConstants";
import { weekdayLabelOfIso } from "@/lib/training";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Sparkles, Clock, MapPin } from "lucide-react";
import { toast } from "sonner";

/**
 * Wiederverwendbarer Dialog zum Duplizieren einer Trainingseinheit auf einen
 * anderen Trainingstermin.
 *
 * Props:
 *   source: { id, training_id, name } — die zu duplizierende Session
 *   trainings: Training[]              — alle Trainings des Teams (für Ziel-Auswahl)
 *   sessions:  Session[]               — alle Sessions des Teams (Ziele mit Session werden ausgeblendet)
 *   todayIso:  "YYYY-MM-DD"            — heutiges Datum, um Vergangenes auszuschließen
 *   onClose:   () => void
 *   onSuccess: (newSession) => void
 */
export default function DuplicateSessionDialog({
  source, trainings, sessions, todayIso, onClose, onSuccess,
}) {
  const [busyId, setBusyId] = useState(null);

  const targets = useMemo(() => {
    if (!source) return [];
    const busyIds = new Set((sessions || []).map((s) => s.training_id));
    return (trainings || [])
      .filter((t) => t.datum >= todayIso && t.id !== source.training_id && !busyIds.has(t.id))
      .sort((a, b) => (a.datum + a.uhrzeit).localeCompare(b.datum + b.uhrzeit));
  }, [source, trainings, sessions, todayIso]);

  const duplicate = async (targetId) => {
    setBusyId(targetId);
    try {
      const { data } = await api.post(
        `/training-sessions/${source.id}/duplicate`,
        { target_training_id: targetId },
      );
      toast.success("Trainingseinheit dupliziert");
      onSuccess?.(data);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        toast.error("Für diesen Termin existiert bereits eine Einheit");
      } else {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Duplizieren fehlgeschlagen");
      }
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={!!source} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle
            className="font-display text-lg font-semibold tracking-tight flex items-center gap-2"
            data-testid="dup-dialog-title"
          >
            <Sparkles className="h-4 w-4 text-brand-lighter" /> Als Vorlage nutzen
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-zinc-400 mb-3">
          Wähle einen Trainingstermin, auf den <span className="text-zinc-100 font-medium">„{source?.name || "diese Einheit"}“</span> kopiert werden soll.
          Nur Termine ohne bestehende Einheit sind möglich. Die Kopie erscheint nicht automatisch in der Bibliothek — erst wenn du sie umbenennst.
        </div>
        <div className="flex-1 overflow-y-auto -mx-1 px-1">
          {targets.length === 0 ? (
            <div className="rounded-md border border-dashed border-zinc-800 bg-zinc-950 p-6 text-center text-sm text-zinc-500">
              Keine passenden zukünftigen Trainingstermine gefunden.<br />
              <span className="text-xs text-zinc-600">Lege zuerst einen neuen Termin an.</span>
            </div>
          ) : (
            <div className="space-y-2" data-testid="dup-targets">
              {targets.map((t) => (
                <button
                  key={t.id}
                  onClick={() => duplicate(t.id)}
                  disabled={busyId !== null}
                  className="w-full text-left rounded-md border border-zinc-800 bg-zinc-950 hover:border-brand-lighter/50 hover:bg-zinc-900 transition p-3 flex items-center gap-3 disabled:opacity-50"
                  data-testid={`dup-target-${t.id}`}
                >
                  <div className="h-11 w-11 rounded-md bg-brand/15 text-brand-lighter flex flex-col items-center justify-center font-display shrink-0">
                    <span className="text-[9px] uppercase tracking-wide">{weekdayLabelOfIso(t.datum)}</span>
                    <span className="text-sm font-bold leading-none">{t.datum.slice(8, 10)}.{t.datum.slice(5, 7)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-zinc-100">{formatDateDE(t.datum)}</div>
                    <div className="text-xs text-zinc-400 flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {t.uhrzeit}</span>
                      {t.ort && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {t.ort}</span>}
                    </div>
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                    {busyId === t.id ? "…" : "Wählen"}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="pt-3 flex justify-end">
          <Button variant="ghost" onClick={onClose} className="text-zinc-400 hover:text-zinc-100" data-testid="dup-close-btn">
            Abbrechen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
